import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message as antdMessage,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, TeamOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import type { SpaceMember, SpaceRole, SpaceUserGroup } from '@lens/shared';
import { ApiError } from '../../../shared/api/client.ts';
import {
  createSpaceUserGroup,
  deleteSpaceUserGroup,
  listMembers,
  listSpaceUserGroups,
  updateSpaceUserGroup,
} from '../api.ts';

/**
 * Вкладка «Группы пользователей»: наборы участников под общим названием
 * («Команда Android»), чтобы заполнять поля типа «участник» одним выбором.
 *
 * К доступу отношения не имеют — это не группы Keycloak со вкладки
 * «Участники». В записи попадают отдельные пользователи, поэтому смена состава
 * группы задним числом уже заполненные поля не трогает.
 */

export interface UserGroupsTabProps {
  spaceId: string;
  /** Роль текущего пользователя: править группы может только админ. */
  myRole: SpaceRole;
}

interface GroupFormValues {
  name: string;
  description?: string;
  userIds: string[];
}

export function UserGroupsTab({ spaceId, myRole }: UserGroupsTabProps): JSX.Element {
  const canManage = myRole === 'admin';
  const [groups, setGroups] = useState<SpaceUserGroup[]>([]);
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SpaceUserGroup | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<GroupFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupsRes, membersRes] = await Promise.all([
        listSpaceUserGroups(spaceId),
        listMembers(spaceId),
      ]);
      setGroups(groupsRes.groups);
      setMembers(membersRes.members);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить группы');
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const memberOptions = useMemo(
    () =>
      members.map((m) => ({
        value: m.userId,
        label: m.displayName || m.login,
      })),
    [members],
  );

  const openCreate = () => {
    form.setFieldsValue({ name: '', description: '', userIds: [] });
    setEditing(null);
    setCreating(true);
  };

  const openEdit = (group: SpaceUserGroup) => {
    form.setFieldsValue({
      name: group.name,
      description: group.description ?? '',
      userIds: group.members.map((m) => m.userId),
    });
    setCreating(false);
    setEditing(group);
  };

  const closeModal = () => {
    setCreating(false);
    setEditing(null);
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        const updated = await updateSpaceUserGroup(spaceId, editing.id, {
          name: values.name,
          description: values.description?.trim() ? values.description.trim() : null,
          userIds: values.userIds ?? [],
        });
        setGroups((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
        antdMessage.success('Группа обновлена');
      } else {
        const created = await createSpaceUserGroup(spaceId, {
          name: values.name,
          description: values.description?.trim() ? values.description.trim() : null,
          userIds: values.userIds ?? [],
        });
        setGroups((prev) => [...prev, created]);
        antdMessage.success('Группа создана');
      }
      closeModal();
    } catch (err) {
      antdMessage.error(err instanceof ApiError ? err.message : 'Не удалось сохранить группу');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (group: SpaceUserGroup) => {
    try {
      await deleteSpaceUserGroup(spaceId, group.id);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      antdMessage.success('Группа удалена');
    } catch (err) {
      antdMessage.error(err instanceof ApiError ? err.message : 'Не удалось удалить группу');
    }
  };

  const columns: ColumnsType<SpaceUserGroup> = [
    {
      title: 'Группа',
      key: 'name',
      render: (_, group) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{group.name}</Typography.Text>
          {group.description ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {group.description}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Кто входит',
      key: 'members',
      render: (_, group) =>
        group.members.length === 0 ? (
          <Typography.Text type="secondary">никого</Typography.Text>
        ) : (
          <Space size={[4, 4]} wrap>
            {group.members.map((m) => (
              <Tag key={m.userId}>{m.displayName || m.login}</Tag>
            ))}
          </Space>
        ),
    },
    ...(canManage
      ? [
          {
            title: '',
            key: 'actions',
            width: 96,
            render: (_: unknown, group: SpaceUserGroup) => (
              <Space size={4}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openEdit(group)}
                  title="Изменить"
                />
                <Popconfirm
                  title="Удалить группу?"
                  description="Уже заполненные поля не изменятся."
                  okText="Удалить"
                  cancelText="Отмена"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => remove(group)}
                >
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Удалить" />
                </Popconfirm>
              </Space>
            ),
          } as ColumnsType<SpaceUserGroup>[number],
        ]
      : []),
  ];

  if (loading) return <Spin />;

  return (
    <div>
      {error ? <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} /> : null}

      <div style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">
          Группа — набор пользователей под общим названием. В поле типа «участник» её видно отдельным
          разделом: выбираете группу — в поле попадают все её участники.
        </Typography.Text>
      </div>

      {canManage ? (
        <Button
          type="primary"
          icon={<UsergroupAddOutlined />}
          onClick={openCreate}
          style={{ marginBottom: 12 }}
          data-testid="user-group-create"
        >
          Создать группу
        </Button>
      ) : null}

      {groups.length === 0 ? (
        <Empty
          image={<TeamOutlined style={{ fontSize: 40, opacity: 0.4 }} />}
          description="Групп пока нет"
        />
      ) : (
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={groups}
          pagination={false}
          data-testid="user-groups-table"
        />
      )}

      <Modal
        open={creating || editing !== null}
        title={editing ? 'Изменить группу' : 'Новая группа'}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={saving}
        onOk={submit}
        onCancel={closeModal}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: 'Введите название' }, { max: 120 }]}
          >
            <Input placeholder="Команда Android" autoFocus />
          </Form.Item>
          <Form.Item name="description" label="Пояснение" rules={[{ max: 500 }]}>
            <Input placeholder="Необязательно" />
          </Form.Item>
          <Form.Item name="userIds" label="Кто входит">
            <Select
              mode="multiple"
              allowClear
              options={memberOptions}
              placeholder="Выберите участников пространства"
              showSearch
              filterOption={(input, opt) =>
                String(opt?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
